import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';

// Route: /events/:id/scan  (already in App.js)
// npm install html5-qrcode  (loaded dynamically below — no import needed)

const ScanTicket = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanResult, setScanResult] = useState(null);
  const [scannerReady, setScannerReady] = useState(false);
  const [scannerStarted, setScannerStarted] = useState(false);
  const [checkedInCount, setCheckedInCount] = useState(0);

  // Load event
  useEffect(() => {
    getDoc(doc(db, 'events', id))
      .then((snap) => { if (snap.exists()) setEvent({ id: snap.id, ...snap.data() }); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  // Load html5-qrcode dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
    script.onload = () => setScannerReady(true);
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
      if (window._qrScanner) window._qrScanner.stop().catch(() => {});
    };
  }, []);

  const startScanner = () => {
    if (!window.Html5Qrcode) return;
    setScannerStarted(true);
    setScanResult(null);

    const scanner = new window.Html5Qrcode('qr-reader');
    window._qrScanner = scanner;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      async (decodedText) => {
        scanner.pause();

        let ticket;
        try {
          ticket = JSON.parse(decodedText);
        } catch {
          setScanResult({ status: 'error', message: 'Invalid QR — not an EventHub ticket.' });
          setTimeout(() => scanner.resume(), 2000);
          return;
        }

        if (ticket.eventId !== id) {
          setScanResult({ status: 'wrong-event', message: 'This ticket is for a different event.' });
          setTimeout(() => scanner.resume(), 2500);
          return;
        }

        try {
          // Check in — update their RSVP doc in Firestore
          const rsvpRef = doc(db, 'events', id, 'rsvps', ticket.userId);
          const rsvpSnap = await getDoc(rsvpRef);

          if (!rsvpSnap.exists()) {
            setScanResult({ status: 'error', message: `${ticket.userName} has no RSVP for this event.` });
            setTimeout(() => scanner.resume(), 3000);
            return;
          }

          if (rsvpSnap.data().checkedIn) {
            setScanResult({ status: 'error', message: `${ticket.userName} is already checked in!` });
            setTimeout(() => scanner.resume(), 3000);
            return;
          }

          await updateDoc(rsvpRef, {
            checkedIn: true,
            checkedInAt: serverTimestamp(),
          });

          setCheckedInCount((c) => c + 1);
          setScanResult({ status: 'success', message: `Welcome, ${ticket.userName}!` });
        } catch (e) {
          setScanResult({ status: 'error', message: 'Check-in failed. Try again.' });
          console.error(e);
        }

        setTimeout(() => { setScanResult(null); scanner.resume(); }, 3000);
      },
      () => {} // ignore frame errors
    ).catch((err) => {
      console.error('Scanner error:', err);
      setScannerStarted(false);
    });
  };

  const stopScanner = () => {
    if (window._qrScanner) window._qrScanner.stop().catch(() => {});
    setScannerStarted(false);
    setScanResult(null);
  };

  const resultColors = {
    success:     { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46', icon: '✅' },
    error:       { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', icon: '❌' },
    'wrong-event': { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', icon: '⚠️' },
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><h2>Loading...</h2></div>;

  // Only the creator can access this page
  if (event && user && event.creatorId !== user.uid) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <h2 style={{ color: '#dc2626' }}>Access Denied</h2>
        <p style={{ color: '#6b7280' }}>Only the event creator can scan tickets.</p>
        <Link to={`/events/${id}`} style={{ color: '#6366f1' }}>← Back to Event</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '500px', margin: '2rem auto', padding: '0 1.5rem' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to={`/events/${id}`} style={{ color: '#6366f1', textDecoration: 'none' }}>
          ← Back to Event
        </Link>
        <h1 style={{ color: '#1f2937', margin: '0.75rem 0 0.25rem' }}>Scan Tickets</h1>
        {event && <p style={{ color: '#6b7280', margin: 0 }}>{event.title}</p>}
      </div>

      {/* Stats */}
      <div style={{
        backgroundColor: '#ede9fe', borderRadius: '8px',
        padding: '1rem', marginBottom: '1.5rem',
        display: 'flex', alignItems: 'center', gap: '0.75rem',
      }}>
        <span style={{ fontSize: '1.5rem' }}>🎫</span>
        <div>
          <p style={{ margin: 0, fontWeight: 'bold', color: '#4c1d95' }}>
            {checkedInCount} checked in this session
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#7c3aed' }}>
            Point camera at a guest's QR code to check them in.
          </p>
        </div>
      </div>

      {/* Scanner */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)', overflow: 'hidden', marginBottom: '1.5rem',
      }}>
        <div id="qr-reader" style={{ width: '100%', display: scannerStarted ? 'block' : 'none' }} />

        {!scannerStarted && (
          <div style={{
            height: '280px', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '1rem', backgroundColor: '#f9fafb',
          }}>
            <span style={{ fontSize: '3rem' }}>📷</span>
            <p style={{ color: '#9ca3af', margin: 0, textAlign: 'center', fontSize: '0.9rem' }}>
              Camera will appear here.<br />Tap "Start Scanning" to begin.
            </p>
          </div>
        )}

        {scanResult && (() => {
          const c = resultColors[scanResult.status];
          return (
            <div style={{
              backgroundColor: c.bg, border: `2px solid ${c.border}`,
              padding: '1.5rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{c.icon}</div>
              <p style={{ color: c.text, fontWeight: 'bold', fontSize: '1.1rem', margin: '0 0 0.25rem' }}>
                {scanResult.message}
              </p>
              {scanResult.status === 'success' && (
                <p style={{ color: c.text, fontSize: '0.85rem', margin: 0 }}>Scanning again in 3 seconds...</p>
              )}
            </div>
          );
        })()}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        {!scannerStarted ? (
          <button onClick={startScanner} disabled={!scannerReady} style={{
            flex: 1, backgroundColor: '#6366f1', color: 'white',
            padding: '0.875rem', border: 'none', borderRadius: '8px',
            fontWeight: 'bold', fontSize: '1rem',
            cursor: scannerReady ? 'pointer' : 'not-allowed', opacity: scannerReady ? 1 : 0.6,
          }}>
            {scannerReady ? '▶ Start Scanning' : 'Loading scanner...'}
          </button>
        ) : (
          <button onClick={stopScanner} style={{
            flex: 1, backgroundColor: '#e5e7eb', color: '#374151',
            padding: '0.875rem', border: 'none', borderRadius: '8px',
            fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer',
          }}>⏹ Stop Scanner</button>
        )}
      </div>

      {/* Instructions */}
      <div style={{
        marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f3f4f6',
        borderRadius: '8px', fontSize: '0.85rem', color: '#6b7280', lineHeight: '1.6',
      }}>
        <strong style={{ color: '#374151' }}>How to use:</strong>
        <ol style={{ margin: '0.5rem 0 0 1.25rem', padding: 0 }}>
          <li>Tap "Start Scanning" and allow camera access.</li>
          <li>Point your camera at a guest's QR code ticket.</li>
          <li>A green confirmation appears for valid tickets.</li>
          <li>Already checked-in guests will show a red warning.</li>
        </ol>
      </div>
    </div>
  );
};

export default ScanTicket;
